import axios, { AxiosInstance, AxiosResponse } from 'axios';

export interface ComboItem {
  itemId: string;
  nomeItemSnapshot: string;
  quantidade: number;
  obrigatorio: boolean;
}

export interface Combo {
  id: string;
  cnpj: string;
  nome: string;
  descricao?: string;
  categoria: string;
  itens: ComboItem[];
  precoBase: number;
  precoPromocional?: number;
  ativo: boolean;
  ordem?: number;
  createdAt: string;
  updatedAt: string;
  imagePath?: string;
  imageUrl?: string;
}

export interface CreateComboRequest {
  nome: string;
  descricao?: string;
  categoria?: string;
  itens: ComboItem[];
  precoBase: number;
  precoPromocional?: number;
  ativo?: boolean;
  ordem?: number;
}

export interface UpdateComboRequest {
  nome?: string;
  descricao?: string;
  categoria?: string;
  itens?: ComboItem[];
  precoBase?: number;
  precoPromocional?: number;
  ativo?: boolean;
  ordem?: number;
}

class ComboService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 10000,
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  async listCombos(cnpj?: string, onlyActive?: boolean): Promise<Combo[]> {
    try {
      const params: Record<string, any> = {};
      if (cnpj) params.cnpj = cnpj;
      if (onlyActive) params.onlyActive = true;

      const response: AxiosResponse<Combo[]> = await this.client.get('/admin/combos', {
        params: Object.keys(params).length ? params : undefined,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao buscar combos');
    }
  }

  async createCombo(data: CreateComboRequest): Promise<Combo> {
    try {
      const response: AxiosResponse<Combo> = await this.client.post('/admin/combos', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao criar combo');
    }
  }

  async updateCombo(id: string, data: UpdateComboRequest): Promise<Combo> {
    try {
      const response: AxiosResponse<Combo> = await this.client.put(`/admin/combos/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar combo');
    }
  }

  async uploadComboImage(id: string, file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response: AxiosResponse<{ imagePath: string }> = await this.client.post(
        `/admin/combos/${id}/image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data.imagePath;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao enviar imagem do combo');
    }
  }

  async deleteCombo(id: string): Promise<void> {
    try {
      await this.client.delete(`/admin/combos/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao deletar combo');
    }
  }
}

export const comboService = new ComboService();
export default comboService;
